import { useState } from "react";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowLeft,
  MapPin,
  Home,
  BedDouble,
  Bath,
  DollarSign,
  Building,
  CheckSquare,
  Award,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Loader2,
  FileText,
  Mail,
  Phone,
} from "lucide-react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useParams, Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/hooks/use-page-title";

interface RealtorListingDetail {
  id: number;
  address: string;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  price: string;
  arv: string | null;
  bedrooms: number | null;
  bathrooms: string | null;
  sqft: number | null;
  lotSize: string | null;
  yearBuilt: number | null;
  description: string | null;
  photos: string[];
  dealNotes: string | null;
  sellerFinancing: boolean;
  estimatedDaysAvailable: number | null;
  status: string;
  mlsNumber: string | null;
  listingType: string | null;
  buyerAgentCommission: string | null;
  colivingSuitability: string | null;
  partnerId: number;
  partnerName: string;
  partnerEmail: string;
  partnerPhone: string | null;
  partnerCompanyName: string | null;
  colivingCertified: boolean;
  certificationDate: string | null;
  // Task #3441 new fields
  coverPhotoUrl: string | null;
  propertyReportUrl: string | null;
  bedroomsAfterConversion: number | null;
  bathroomsAfterConversion: string | null;
  irr10Year: string | null;
  cashOnCashReturn: string | null;
  cashOutlay: string | null;
  monthlyCashFlow: string | null;
  dealType: string | null;
  dateListed: string | null;
}

function formatCurrency(amount: number | string | null | undefined): string {
  if (amount == null) return "—";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (!isFinite(num) || num === 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
}

function formatPercent(val: number | string | null | undefined): string {
  if (val == null) return "—";
  const num = typeof val === "string" ? parseFloat(val) : val;
  if (!isFinite(num) || num === 0) return "—";
  return `${num}%`;
}

const COLIVING_LABELS: Record<string, string> = {
  yes_already_operating: "Co-living active",
  yes_suitable_not_converted: "Co-living suitable",
  needs_conversion: "Needs conversion",
  unsure: "Unknown",
};

const LISTING_TYPE_LABELS: Record<string, string> = {
  mls: "MLS",
  off_market: "Off Market",
  pocket: "Pocket",
};

function DetailRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value == null || value === "" || value === 0 || value === "0") return null;
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );
}

export default function AppRealtorListingDetail() {
  const queryClient = useQueryClient();
  usePageTitle("Realtor Listing");
  const [, navigate] = useLocation();
  const params = useParams<{ id: string }>();
  const listingId = parseInt(params.id || "0", 10);
  const { toast } = useToast();

  const [showRequest, setShowRequest] = useState(false);
  const [message, setMessage] = useState("");
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const { data: listing, isLoading } = useQuery<RealtorListingDetail>({
    queryKey: ["/api/app/realtor-listings", listingId],
    enabled: listingId > 0,
  });

  const requestMutation = useMutation({
    mutationFn: async (msg: string) => {
      const res = await apiRequest("POST", `/api/app/realtor-listings/${listingId}/request-showing`, { message: msg });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Showing request sent", description: "The realtor will confirm your appointment." });
      setShowRequest(false);
      setMessage("");
      navigate("/app/connections");
    },
    onError: () => toast({ title: "Failed to send request", variant: "destructive" }),
  });

  const { data: copyCheck } = useQuery<{ propertyId: number | null }>({
    queryKey: ["/api/app/underwriting/copy-check", "realtor", listingId],
    queryFn: async () => {
      const res = await fetch(`/api/app/underwriting/copy-check?sourceType=realtor&sourceListingId=${listingId}`, { credentials: "include" });
      return res.json();
    },
    enabled: listingId > 0,
  });
  const alreadyCopied = copyCheck?.propertyId != null;

  const copyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/underwriting/copy-from-listing", { sourceType: "realtor", sourceListingId: listingId });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting/copy-check", "realtor", listingId] });
      navigate(`/app/underwriting/${data.propertyId}`);
    },
    onError: () => toast({ title: "Could not copy to underwriting", variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <AppLayout>
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-72 w-full rounded-xl" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!listing) {
    return (
      <AppLayout>
        <div className="max-w-5xl mx-auto px-4 py-12 text-center">
          <Home className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Listing not found</h2>
          <p className="text-gray-500 mb-4">This listing may have been removed or is no longer available.</p>
          <Button asChild data-testid="button-back-to-buy">
            <Link href="/app/buy"><ArrowLeft className="h-4 w-4 mr-2" /> Back to Buy</Link>
          </Button>
        </div>
      </AppLayout>
    );
  }

  const basePhotos = (listing.photos ?? []).filter(Boolean);
  const photos = listing.coverPhotoUrl && !basePhotos.includes(listing.coverPhotoUrl)
    ? [listing.coverPhotoUrl, ...basePhotos]
    : basePhotos;
  const hasPhotos = photos.length > 0;
  const hasMany = photos.length > 1;
  const safeIdx = Math.min(photoIdx, Math.max(photos.length - 1, 0));
  const goPrev = () => setPhotoIdx((i) => (i - 1 + photos.length) % photos.length);
  const goNext = () => setPhotoIdx((i) => (i + 1) % photos.length);

  const initials = (listing.partnerName || "R")
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <ViewOnlyBanner area="properties" />
        <Link
          href="/app/buy"
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-6 transition-colors"
          data-testid="button-back-to-buy"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Buy
        </Link>

        <div className="relative rounded-xl overflow-hidden mb-4 bg-gradient-to-br from-slate-100 to-slate-200" style={{ minHeight: "24rem" }} data-tour="realtor-listing-detail-photos">
          {hasPhotos ? (
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="block w-full h-full"
              data-testid="button-open-gallery"
              aria-label="Open photo gallery"
            >
              <img
                src={photos[safeIdx]}
                alt={`${listing.address} photo ${safeIdx + 1}`}
                className="w-full h-72 md:h-96 object-cover"
              />
            </button>
          ) : (
            <div className="w-full h-72 md:h-96 flex items-center justify-center">
              <Home className="h-16 w-16 text-slate-300" />
            </div>
          )}
          {hasMany && (
            <>
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow"
                aria-label="Previous photo"
                data-testid="button-prev-photo"
              >
                <ChevronLeft className="h-5 w-5 text-gray-900" />
              </button>
              <button
                type="button"
                onClick={goNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow"
                aria-label="Next photo"
                data-testid="button-next-photo"
              >
                <ChevronRight className="h-5 w-5 text-gray-900" />
              </button>
              <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-medium rounded-full px-3 py-1" data-testid="text-photo-count">
                {safeIdx + 1} / {photos.length}
              </div>
            </>
          )}
          <div className="absolute top-3 left-3 flex items-center gap-1.5">
            <Badge className="bg-violet-500 text-white border-0 shadow-sm text-xs font-semibold px-2.5 py-1">
              Realtor
            </Badge>
            {listing.listingType && (
              <Badge className="bg-white/90 text-gray-800 border-0 shadow-sm text-xs font-medium px-2 py-0.5">
                {LISTING_TYPE_LABELS[listing.listingType] || listing.listingType}
              </Badge>
            )}
          </div>
        </div>

        {hasMany && (
          <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="thumbnail-strip">
            {photos.slice(0, 12).map((p, i) => (
              <button
                key={`${p}-${i}`}
                type="button"
                onClick={() => setPhotoIdx(i)}
                className={`flex-shrink-0 h-16 w-24 rounded overflow-hidden border-2 transition-all ${
                  i === safeIdx ? "border-violet-500" : "border-transparent opacity-70 hover:opacity-100"
                }`}
                aria-label={`Show photo ${i + 1}`}
                data-testid={`button-thumb-${i}`}
              >
                <img src={p} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
            {photos.length > 12 && (
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="flex-shrink-0 h-16 w-24 rounded bg-gray-100 hover:bg-gray-200 text-xs font-medium text-gray-700 flex items-center justify-center"
                data-testid="button-more-photos"
              >
                +{photos.length - 12}
              </button>
            )}
          </div>
        )}

        <div className="flex items-start justify-between mb-6 gap-4">
          <div data-tour="realtor-listing-detail-heading">
            <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="text-listing-address">
              {listing.address}
            </h1>
            <p className="text-gray-500 flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              {[listing.city, listing.state, listing.zipCode].filter(Boolean).join(", ")}
            </p>
            <p className="text-3xl font-bold text-gray-900 mt-3" data-testid="text-listing-price">
              {formatCurrency(listing.price)}
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
              {listing.bedrooms != null && (
                <span className="flex items-center gap-1"><BedDouble className="h-4 w-4" />{listing.bedrooms} bd</span>
              )}
              {listing.bathrooms != null && (
                <span className="flex items-center gap-1"><Bath className="h-4 w-4" />{listing.bathrooms} ba</span>
              )}
              {listing.sqft != null && (
                <span className="flex items-center gap-1"><Home className="h-4 w-4" />{listing.sqft.toLocaleString()} sqft</span>
              )}
            </div>
            {listing.colivingSuitability && (
              <div className="flex items-center gap-1 mt-3" data-tour="realtor-listing-detail-suitability">
                <CheckSquare className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-700">{COLIVING_LABELS[listing.colivingSuitability] || listing.colivingSuitability}</span>
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="realtor-listing-detail" />
              <GuideButton pageKey="realtor-listing-detail" />
            </div>
            <Button
              onClick={() => setShowRequest(true)}
              data-testid="button-request-showing"
              data-tour="realtor-listing-detail-request"
              className="bg-violet-600 hover:bg-violet-700"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Request Showing
            </Button>
            <Button
              variant={alreadyCopied ? "default" : "outline"}
              onClick={() => {
                if (alreadyCopied && copyCheck?.propertyId) {
                  navigate(`/app/underwriting/${copyCheck.propertyId}`);
                } else {
                  copyMutation.mutate();
                }
              }}
              disabled={copyMutation.isPending}
              data-testid="button-underwrite-myself-realtor"
              data-tour="realtor-listing-detail-underwrite"
            >
              {copyMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Copying...</>
              ) : alreadyCopied ? (
                "Review in Underwriting"
              ) : (
                "Underwrite myself"
              )}
            </Button>
          </div>
        </div>
        <GuideNudgeBanner pageKey="realtor-listing-detail" />

        {listing.description && (
          <div className="bg-white border border-gray-100 rounded-xl p-5 mb-6" data-tour="realtor-listing-detail-info">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Description
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line" data-testid="text-listing-description">
              {listing.description}
            </p>
          </div>
        )}

        {listing.dealNotes && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 mb-6">
            <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-amber-600" />
              Deal Notes
            </h3>
            <p className="text-sm text-amber-900 leading-relaxed whitespace-pre-line" data-testid="text-deal-notes">
              {listing.dealNotes}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white border border-gray-100 rounded-xl p-5" data-tour="realtor-listing-detail-model">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Building className="h-4 w-4 text-gray-400" />
              Property Facts
            </h3>
            <DetailRow label="Bedrooms (current)" value={listing.bedrooms} />
            <DetailRow label="Bedrooms after conversion" value={listing.bedroomsAfterConversion} />
            <DetailRow label="Bathrooms (current)" value={listing.bathrooms} />
            <DetailRow label="Bathrooms after conversion" value={listing.bathroomsAfterConversion} />
            <DetailRow label="Square Footage" value={listing.sqft ? `${listing.sqft.toLocaleString()} sqft` : null} />
            <DetailRow label="Lot Size" value={listing.lotSize ? `${listing.lotSize} acres` : null} />
            <DetailRow label="Year Built" value={listing.yearBuilt} />
            <DetailRow label="Deal Type" value={listing.dealType} />
            <DetailRow label="Date Listed" value={listing.dateListed} />
            <DetailRow label="MLS #" value={listing.mlsNumber} />
            <DetailRow label="Listing Type" value={listing.listingType ? (LISTING_TYPE_LABELS[listing.listingType] || listing.listingType) : null} />
            <DetailRow label="Co-living Suitability" value={listing.colivingSuitability ? (COLIVING_LABELS[listing.colivingSuitability] || listing.colivingSuitability) : null} />
          </div>

          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-gray-400" />
              Financials
            </h3>
            <DetailRow label="List Price" value={formatCurrency(listing.price)} />
            <DetailRow label="ARV (After Repair Value)" value={formatCurrency(listing.arv)} />
            <DetailRow label="Cash Outlay" value={formatCurrency(listing.cashOutlay)} />
            <DetailRow label="Monthly Cash Flow" value={listing.monthlyCashFlow ? formatCurrency(listing.monthlyCashFlow) : null} />
            <DetailRow label="10-Year IRR" value={listing.irr10Year ? `${(parseFloat(listing.irr10Year) * 100).toFixed(2)}%` : null} />
            <DetailRow label="Cash-on-Cash Return" value={listing.cashOnCashReturn ? `${(parseFloat(listing.cashOnCashReturn) * 100).toFixed(2)}%` : null} />
            <DetailRow label="Buyer Agent Commission" value={formatPercent(listing.buyerAgentCommission)} />
            <DetailRow label="Seller Financing" value={listing.sellerFinancing ? "Available" : null} />
            <DetailRow label="Estimated Days Available" value={listing.estimatedDaysAvailable} />
          </div>
        </div>

        {listing.propertyReportUrl && (
          <div className="bg-white border border-gray-100 rounded-xl p-5 mb-6" data-tour="realtor-listing-detail-docs">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Documents
            </h3>
            <a
              href={listing.propertyReportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-brand hover:underline font-medium"
              data-testid="link-property-report"
            >
              <FileText className="h-4 w-4" />
              View Property Report PDF
            </a>
          </div>
        )}

        <div className="bg-white border border-gray-100 rounded-xl p-5 mb-8">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Award className="h-4 w-4 text-violet-500" />
            Listing Partner
          </h3>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 text-base font-bold">
              {initials}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-gray-900" data-testid="text-partner-name">{listing.partnerName}</p>
                {listing.colivingCertified && (
                  <Badge className="bg-green-100 text-green-800 border-0 text-xs">
                    <Award className="h-3 w-3 mr-1" />
                    Co-living Certified
                  </Badge>
                )}
              </div>
              {listing.partnerCompanyName && (
                <p className="text-sm text-gray-500" data-testid="text-partner-company">{listing.partnerCompanyName}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{listing.partnerEmail}</span>
                {listing.partnerPhone && (
                  <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{listing.partnerPhone}</span>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRequest(true)}
              data-testid="button-request-showing-partner"
            >
              <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
              Request Showing
            </Button>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4 mb-8">
          <p className="text-xs text-gray-400 leading-relaxed" data-testid="text-disclaimer">
            * All property details, photos, and financial information shown on this page are provided by the listing realtor and have not been independently verified by PadPulse. Buyers should conduct their own due diligence and consult qualified professionals before making any purchase decision.
          </p>
        </div>
      </div>

      <Dialog open={showRequest} onOpenChange={setShowRequest}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request a showing</DialogTitle>
            <DialogDescription>{listing.address} · {formatCurrency(listing.price)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="showing-msg">Your message</Label>
              <Textarea
                id="showing-msg"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="Hi, I'd like to schedule a showing for..."
                data-testid="input-showing-message"
              />
            </div>
            <Button
              className="w-full"
              onClick={() => { if (message.trim()) requestMutation.mutate(message.trim()); }}
              disabled={!message.trim() || requestMutation.isPending}
              data-testid="button-send-showing"
            >
              {requestMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Send request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent
          className="max-w-5xl p-0 bg-black border-0 overflow-hidden"
          data-testid="lightbox"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{listing.address} photos</DialogTitle>
            <DialogDescription>Photo {safeIdx + 1} of {photos.length}</DialogDescription>
          </DialogHeader>
          <div className="relative w-full bg-black flex items-center justify-center" style={{ minHeight: "60vh" }}>
            {hasPhotos && (
              <img
                src={photos[safeIdx]}
                alt={`${listing.address} photo ${safeIdx + 1}`}
                className="max-h-[80vh] max-w-full object-contain"
                data-testid="lightbox-image"
              />
            )}
            {hasMany && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow"
                  aria-label="Previous photo"
                  data-testid="button-lightbox-prev"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-900" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow"
                  aria-label="Next photo"
                  data-testid="button-lightbox-next"
                >
                  <ChevronRight className="h-5 w-5 text-gray-900" />
                </button>
              </>
            )}
            <div className="absolute top-3 left-3 bg-black/60 text-white text-xs font-medium rounded-full px-3 py-1">
              {safeIdx + 1} / {photos.length}
            </div>
          </div>
          {hasMany && (
            <div className="flex gap-2 p-3 bg-black/95 overflow-x-auto">
              {photos.map((p, i) => (
                <button
                  key={`lb-${p}-${i}`}
                  type="button"
                  onClick={() => setPhotoIdx(i)}
                  className={`flex-shrink-0 h-14 w-20 rounded overflow-hidden border-2 transition-all ${
                    i === safeIdx ? "border-violet-400" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                  aria-label={`Show photo ${i + 1}`}
                  data-testid={`button-lightbox-thumb-${i}`}
                >
                  <img src={p} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
